/**
 * <jgd-cart-delivery-date>
 *
 * Replaces a server-rendered "min–max Tage" fallback with concrete calendar
 * dates ("Sa. 25.07. – Mi. 29.07."), computed client-side so cached/
 * server-rendered cart HTML can never show stale dates. Mirrors the date
 * formatting logic in jgd-availability.js (the PDP block) so both surfaces
 * render identically.
 *
 * This auto-upgrades on insertion via the CustomElementRegistry, so a fresh
 * element (e.g. this line switching from "in stock" to "exceeds stock",
 * which swaps in a whole new element per jgd-cart-delivery.liquid) renders
 * correctly without extra wiring.
 *
 * The tricky part is cart-items-component (component-cart-items.js): it
 * updates the cart via a section-rendering round trip + `morphSection`,
 * which reconciles this element's live DOM against fresh server HTML that
 * still contains the *static* "min–max Tage" fallback (our JS-computed
 * dates were never sent to the server). Neither observedAttributes /
 * attributeChangedCallback nor deferring the re-render to a microtask turned
 * out to reliably win that race — morphSection's own content patch can land
 * after ours regardless of timing (view-transition-based morphing doesn't
 * run purely synchronously). So instead of racing it, a MutationObserver
 * watches this element's own text/attributes and re-renders whenever they
 * get changed out from under us by something that isn't this class itself —
 * that's correct no matter *when* or *how* the outside change happens.
 *
 * data-cutoff="true" applies the same in-stock order cutoff as the PDP:
 * orders placed at/after ORDER_CUTOFF_HOUR (customer's local time) are
 * treated as placed the next day, shifting the window by one day. The
 * Liquid side only sets this to "true" for the short (in-stock) window —
 * never for the backorder/long window, which is a fixed lead time.
 */

/** Keep in sync with ORDER_CUTOFF_HOUR in jgd-availability.js (PDP). */
const ORDER_CUTOFF_HOUR = 11;

class JgdCartDeliveryDate extends HTMLElement {
  /** @type {MutationObserver|null} */
  #observer = null;

  /** Text this element should currently show, per its own data attributes —
   *  used to tell "an outside change put back the wrong text" apart from
   *  "our own #render() just ran", so the observer doesn't loop forever. */
  #expectedText = '';

  connectedCallback() {
    this.#render();

    this.#observer = new MutationObserver(() => this.#handleMutation());
    this.#observer.observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-min-days', 'data-max-days', 'data-cutoff', 'data-locale'],
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleMutation() {
    // Whatever changed — attributes patched by a morph, or text content
    // patched back to the static fallback — re-render from the (now
    // current) data attributes. If that produces the same text we already
    // expect, this is a no-op past the initial comparison; no infinite loop.
    if (this.textContent !== this.#expectedText || this.#computedFromAttributes() !== this.#expectedText) {
      this.#render();
    }
  }

  get #locale() {
    return this.dataset.locale || document.documentElement.lang || 'de';
  }

  #int(name, fallback = 0) {
    const value = Number(this.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  /** "Sa. 25.07." — assembled from Intl parts so it works for any locale. */
  #formatDate(date) {
    const weekday = new Intl.DateTimeFormat(this.#locale, { weekday: 'short' }).format(date);
    const day = new Intl.DateTimeFormat(this.#locale, { day: '2-digit', month: '2-digit' }).format(date);
    const separator = weekday.endsWith('.') ? ' ' : '. ';
    return `${weekday}${separator}${day}`;
  }

  /** Fresh Date instance `days` days from now (today + days). */
  #addDays(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  /** No Sunday deliveries — if a computed date lands on one, push it to
   *  Monday. Keep in sync with #skipSunday in jgd-availability.js (PDP). */
  #skipSunday(date) {
    const result = new Date(date);
    if (result.getDay() === 0) result.setDate(result.getDate() + 1);
    return result;
  }

  #computedFromAttributes() {
    let minDays = this.#int('minDays');
    let maxDays = this.#int('maxDays', minDays + 1);

    if (this.dataset.cutoff === 'true' && new Date().getHours() >= ORDER_CUTOFF_HOUR) {
      minDays += 1;
      maxDays += 1;
    }

    // Check each end of the window independently — only one of them might
    // land on a Sunday.
    const from = this.#skipSunday(this.#addDays(minDays));
    const to = this.#skipSunday(this.#addDays(maxDays));

    return `${this.#formatDate(from)} – ${this.#formatDate(to)}`;
  }

  #render() {
    this.#expectedText = this.#computedFromAttributes();
    if (this.textContent !== this.#expectedText) this.textContent = this.#expectedText;
  }
}

if (!customElements.get('jgd-cart-delivery-date')) {
  customElements.define('jgd-cart-delivery-date', JgdCartDeliveryDate);
}
